import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Users, TrendingUp, Award } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import SEO, { SEO_CONFIGS } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [jobs, setJobs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchJobs();
    fetchTestimonials();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const sectors = [
    {
      name: 'Manufacturing',
      image: 'https://images.unsplash.com/photo-1681263576084-e054b2d89903?q=80&w=800&auto=format&fit=crop',
      description: 'Leadership for manufacturing excellence'
    },
    {
      name: 'Engineering',
      image: 'https://images.unsplash.com/photo-1771433050765-72a5eb1658ca?q=80&w=800&auto=format&fit=crop',
      description: 'Technical leaders who innovate'
    },
    {
      name: 'Automation',
      image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?q=80&w=800&auto=format&fit=crop',
      description: 'Future-ready automation experts'
    },
    {
      name: 'Fashion & Apparel',
      image: 'https://images.unsplash.com/photo-1762430815620-fcca603c240c?q=80&w=800&auto=format&fit=crop',
      description: 'Creative visionaries for fashion brands'
    },
    {
      name: 'Energy',
      image: 'https://images.unsplash.com/photo-1709620292053-f3dc6420a79a?q=80&w=800&auto=format&fit=crop',
      description: 'Energy sector transformation leaders'
    },
    {
      name: 'Emerging Technology',
      image: 'https://images.unsplash.com/photo-1769467701165-a83217c13446?q=80&w=800&auto=format&fit=crop',
      description: 'Pioneers in emerging tech domains'
    }
  ];

  const stats = [
    { icon: <Briefcase size={32} />, value: '500+', label: 'Successful Placements' },
    { icon: <Users size={32} />, value: '100+', label: 'Corporate Clients' },
    { icon: <TrendingUp size={32} />, value: '95%', label: 'Client Satisfaction' },
    { icon: <Award size={32} />, value: '10+', label: 'Years Experience' }
  ];

  return (
    <div>
      <SEO {...SEO_CONFIGS.home} />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1714601344981-75e003bc5d18?q=80&w=2000&auto=format&fit=crop)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-slate-900/80 z-10" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
            data-testid="hero-title"
          >
            Finding Leaders Who <span className="gold-text">Transform Organizations</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg sm:text-xl text-slate-200 mb-8 max-w-3xl mx-auto"
            data-testid="hero-subtitle"
          >
            Vocavi Partners specializes in executive search and leadership recruitment across Manufacturing, Engineering, Fashion, Energy, and Emerging Technology sectors.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/jobs"
              data-testid="hero-cta-explore-jobs"
              className="bg-secondary text-primary hover:bg-secondary/90 px-8 py-4 uppercase tracking-widest text-sm font-semibold transition-all inline-flex items-center justify-center"
            >
              Explore Opportunities <ArrowRight className="ml-2" size={18} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-4 uppercase tracking-widest text-sm font-semibold transition-all inline-flex items-center justify-center"
            >
              Partner With Us
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-spacing bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 mb-4 gold-text">
                  {stat.icon}
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section className="section-spacing bg-muted" data-testid="sectors-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-4">
              Sectors We Serve
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Deep expertise across key industries driving India's growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sectors.map((sector, index) => (
              <div
                key={index}
                className="sector-card group cursor-pointer"
                data-testid={`sector-card-${index}`}
              >
                <img
                  src={sector.image}
                  alt={sector.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">{sector.name}</h3>
                  <p className="text-slate-300 text-sm">{sector.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/sectors"
              data-testid="sectors-view-all"
              className="inline-flex items-center text-primary font-semibold hover:text-secondary transition-colors"
            >
              View All Sectors <ArrowRight className="ml-2" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Jobs Section */}
      <section className="section-spacing bg-white" data-testid="jobs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-4">
              Current Opportunities
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore executive and leadership positions across industries
            </p>
          </div>

          <div className="space-y-4">
            {jobs.map((job) => (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                data-testid={`job-card-${job.id}`}
                className="job-card block p-6 hover:shadow-md transition-all"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-primary mb-2">{job.title}</h3>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span>{job.location}</span>
                      <span>•</span>
                      <span>{job.sector}</span>
                      <span>•</span>
                      <span>{job.experience}</span>
                    </div>
                  </div>
                  <div className="mt-4 lg:mt-0">
                    <span className="inline-flex items-center text-secondary font-semibold">
                      View Details <ArrowRight className="ml-2" size={18} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/jobs"
              data-testid="jobs-view-all"
              className="bg-primary text-white hover:bg-primary/90 px-8 py-4 uppercase tracking-widest text-sm font-semibold transition-all inline-flex items-center"
            >
              View All Positions <ArrowRight className="ml-2" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Only show if testimonials exist */}
      {testimonials.length > 0 && (
        <section className="section-spacing bg-primary text-white" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold mb-4 gold-text">
                Client Testimonials
              </h2>
              <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                Trusted by leading organizations across industries
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-slate-800/50 p-8 border border-slate-700 hover:border-secondary transition-colors"
                  data-testid={`testimonial-${testimonial.id}`}
                >
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="gold-text text-xl">★</span>
                    ))}
                  </div>
                  <p className="text-slate-200 mb-6 italic">&ldquo;{testimonial.content}&rdquo;</p>
                  <div>
                    <div className="font-bold text-white">{testimonial.name}</div>
                    <div className="text-sm text-slate-400">
                      {testimonial.position}, {testimonial.company}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section-spacing bg-secondary" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
            Ready to Find Your Next Leader?
          </h2>
          <p className="text-lg text-primary/80 mb-8">
            Partner with Vocavi Partners for executive search excellence
          </p>
          <Link
            to="/contact"
            data-testid="cta-contact-button"
            className="bg-primary text-white hover:bg-primary/90 px-10 py-5 uppercase tracking-widest text-sm font-semibold transition-all inline-block"
          >
            Get In Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
